/**
 * Eén gedeelde toestand voor alle schermen: wie ben ik, welke boot staat open, hoe
 * ziet de hiërarchie eruit en welke kar is gekozen.
 *
 * Alles wat berekend is — maten, foutmeldingen, uren — komt van de server en wordt
 * hier niet nagerekend. Na elke wijziging wordt opnieuw opgehaald, zodat de browser
 * niet uit de pas kan lopen met wat de engine ervan maakt.
 *
 * Wat een rol niet mag zien komt niet mee in het antwoord. `rechten` is er dus alleen
 * om knoppen en kolommen te verbergen; de API blijft de poortwachter.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import {
  ApiFout,
  api,
  type Bouwnummer,
  type Ik,
  type Kleuren,
  type NavBouwelement,
  type NavKar,
  type Navigatie,
  type Recht,
  type Rechten,
} from './api';
import { haalProfiel, type Profiel } from './profiel';

const GEEN_RECHTEN = {} as Rechten;

/** Een kar met het bouwelement en de plaats waar hij hangt — voor het kruimelpad. */
/** Eén kar met zijn plek in de boom, voor het kruimelpad en de karkiezer. */
export interface KarInBoom {
  kar: NavKar;
  deck: string;
  sectie: string;
  ruimte: string;
  ruimteNaam: string;
}

interface Toestand {
  ik: Ik | null;
  /**
   * Naam, e-mailadres en eventueel de foto van de aangemelde gebruiker. Komt van de
   * aanmelding zelf (`/.auth/me`), niet van de API: die kent alleen het UPN.
   */
  profiel: Profiel | null;
  mag: (recht: Recht) => boolean;
  kleuren: Kleuren | null;
  /** De kleur van een afdeling, uit de enige kleurbron: de API. */
  afdelingskleur: (afdeling: string) => { kleur: string; tekst: string };

  bouwnummers: Bouwnummer[];
  boot: Bouwnummer | null;
  kiesBoot: (nr: string) => void;

  navigatie: Navigatie | null;
  /** Alle karren van deze boot, plat, met hun plaats in de hiërarchie. */
  karren: KarInBoom[];
  karId: string | null;
  kiesKar: (id: string) => void;
  huidigeKar: KarInBoom | null;

  laden: boolean;
  nietIngelogd: boolean;
  fout: string | null;
  meldFout: (bericht: string | null) => void;
  /** Opnieuw ophalen. `ververstOp` verandert daarmee ook. */
  ververs: () => Promise<void>;
  ververstOp: Date | null;
  /**
   * Gaat omhoog zodra er ergens in deze boot iets is gewijzigd — ook door een andere
   * gebruiker. Schermen zonder onbewaard werk zetten dit in hun ophaal-effect, zodat
   * ze zichzelf verversen. Schermen waar wél iets in bewerking kan zijn (planning,
   * bewerkingen) tonen liever een melding dan dat ze het werk overschrijven.
   */
  wijzigingsteller: number;
}

const Context = createContext<Toestand | null>(null);

const BEWAARD_BOOT = 'houtstaat.boot';
const BEWAARD_KAR = 'houtstaat.kar';

/** De karren uit de boom platgeslagen, met hun deck, sectie en ruimte erbij. */
function karrenUitBoom(navigatie: Navigatie | null): KarInBoom[] {
  if (!navigatie) return [];
  const uit: KarInBoom[] = [];
  for (const deck of navigatie.decks) {
    for (const sectie of deck.secties) {
      for (const ruimte of sectie.ruimtes) {
        for (const kar of ruimte.karren) {
          uit.push({
            kar,
            deck: deck.deck,
            sectie: sectie.sectie,
            ruimte: ruimte.ruimte,
            ruimteNaam: ruimte.naam,
          });
        }
      }
    }
  }
  return uit;
}

export function GegevensProvider({ children }: { children: ReactNode }) {
  const [ik, setIk] = useState<Ik | null>(null);
  const [profiel, setProfiel] = useState<Profiel | null>(null);
  const [kleuren, setKleuren] = useState<Kleuren | null>(null);
  const [bouwnummers, setBouwnummers] = useState<Bouwnummer[]>([]);
  const [bootNr, setBootNr] = useState<string | null>(() => localStorage.getItem(BEWAARD_BOOT));
  const [navigatie, setNavigatie] = useState<Navigatie | null>(null);
  const [karId, setKarId] = useState<string | null>(() => localStorage.getItem(BEWAARD_KAR));
  const [laden, setLaden] = useState(true);
  const [nietIngelogd, setNietIngelogd] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [ververstOp, setVerverstOp] = useState<Date | null>(null);
  const [wijzigingsteller, setWijzigingsteller] = useState(0);
  const stempel = useRef<string | null>(null);

  // De navigatie van de laatst gekozen boot wordt meteen meegevraagd, naast de
  // gebruiker en de bootlijst. Zonder dat zou het scherm twee keer op de server
  // moeten wachten: eerst wie ben ik, dan pas de gegevens.
  const voorgeladen = useRef<string | null>(null);

  useEffect(() => {
    let afgebroken = false;
    void (async () => {
      const opgeslagen = localStorage.getItem(BEWAARD_BOOT);
      try {
        const [mij, palet, lijst, vroeg] = await Promise.all([
          api.ik(),
          api.kleuren(),
          api.bouwnummers(),
          opgeslagen ? api.navigatie(opgeslagen).catch(() => null) : Promise.resolve(null),
        ]);
        if (afgebroken) return;
        setIk(mij);
        // Het profiel hangt niet aan de gegevens van de boot: het scherm mag er niet
        // op wachten. Komt het niet, dan blijft de UPN-terugval staan.
        void haalProfiel(mij.naam).then((p) => {
          if (!afgebroken) setProfiel(p);
        });
        setKleuren(palet);
        setBouwnummers(lijst);

        const gekozen =
          opgeslagen && lijst.some((b) => b.bouwnummer === opgeslagen)
            ? opgeslagen
            : (lijst[0]?.bouwnummer ?? null);

        if (vroeg && gekozen === opgeslagen) {
          voorgeladen.current = opgeslagen;
          setNavigatie(vroeg);
          setVerverstOp(new Date());
        }
        setBootNr(gekozen);
      } catch (e) {
        if (afgebroken) return;
        // De statische bestanden zijn vrij toegankelijk, de gegevens niet. Een 401
        // van de API betekent dus simpelweg: nog niet aangemeld.
        if (e instanceof ApiFout && e.status === 401) setNietIngelogd(true);
        else setFout(e instanceof Error ? e.message : 'De applicatie kon niet laden.');
      } finally {
        if (!afgebroken) setLaden(false);
      }
    })();
    return () => {
      afgebroken = true;
    };
  }, []);

  const ververs = useCallback(async () => {
    if (!bootNr) return;
    try {
      setNavigatie(await api.navigatie(bootNr));
      setVerverstOp(new Date());
      setFout(null);
    } catch (e) {
      // Een tekenaar of productiemedewerker mag de navigatie wel zien; loopt het
      // toch mis, dan is dat een echte fout en geen lege boom.
      setFout(e instanceof Error ? e.message : 'De gegevens konden niet geladen worden.');
    }
  }, [bootNr]);

  useEffect(() => {
    if (!bootNr) return;
    localStorage.setItem(BEWAARD_BOOT, bootNr);
    if (voorgeladen.current === bootNr) {
      voorgeladen.current = null;
      return;
    }
    setLaden(true);
    void ververs().finally(() => setLaden(false));
  }, [bootNr, ververs]);

  /*
   * Realtime verversen. Elke acht seconden een stempel van een paar honderd bytes; is
   * die anders dan de vorige, dan is er iets gewijzigd en verversen de schermen.
   *
   * Het tabblad op de achtergrond pollen heeft geen zin — dan kijkt er niemand — dus
   * dat slaan we over en halen we bij terugkomst één keer op.
   */
  useEffect(() => {
    if (!bootNr) return;
    let gestopt = false;

    const kijk = async () => {
      if (gestopt || document.hidden) return;
      try {
        const { stempel: nieuw } = await api.wijzigingen(bootNr);
        if (gestopt) return;
        if (stempel.current !== null && stempel.current !== nieuw) {
          setWijzigingsteller((n) => n + 1);
          await ververs();
        }
        stempel.current = nieuw;
      } catch {
        // Een mislukte controle is geen fout om te melden: de volgende ronde probeert
        // het opnieuw. Een echte storing merkt de gebruiker bij zijn eerstvolgende
        // handeling.
      }
    };

    void kijk();
    const tikker = window.setInterval(() => void kijk(), 8000);
    const bijTerugkomst = () => {
      if (!document.hidden) void kijk();
    };
    document.addEventListener('visibilitychange', bijTerugkomst);
    return () => {
      gestopt = true;
      window.clearInterval(tikker);
      document.removeEventListener('visibilitychange', bijTerugkomst);
    };
  }, [bootNr, ververs]);

  const karren = useMemo(() => karrenUitBoom(navigatie), [navigatie]);

  // Staat de bewaarde kar niet (meer) in deze boot, dan valt de keuze terug op de
  // eerste kar met onderdelen, en anders op de eerste kar. Zo blijft het scherm werken
  // na het splitsen of wisselen van boot, en opent het niet op een lege kar terwijl er
  // wel een houtstaat is.
  const huidigeKar = useMemo(
    () =>
      karren.find((k) => k.kar.id === karId) ??
      karren.find((k) => k.kar.onderdelen > 0) ??
      karren[0] ??
      null,
    [karren, karId],
  );

  const kiesKar = useCallback((id: string) => {
    setKarId(id);
    localStorage.setItem(BEWAARD_KAR, id);
  }, []);

  const boot = useMemo(
    () => bouwnummers.find((b) => b.bouwnummer === bootNr) ?? navigatie?.bouwnummer ?? null,
    [bouwnummers, bootNr, navigatie],
  );

  const mag = useCallback((recht: Recht) => Boolean(ik?.rechten[recht]), [ik]);

  const afdelingskleur = useCallback(
    (afdeling: string) => {
      const gevonden = kleuren?.afdelingen.find((a) => a.afdeling === afdeling);
      return gevonden ?? { kleur: 'var(--muted-licht)', tekst: '#ffffff' };
    },
    [kleuren],
  );

  const waarde = useMemo<Toestand>(
    () => ({
      ik,
      mag,
      kleuren,
      afdelingskleur,
      bouwnummers,
      boot,
      kiesBoot: setBootNr,
      navigatie,
      karren,
      profiel,
      karId: huidigeKar?.kar.id ?? null,
      kiesKar,
      huidigeKar,
      laden,
      nietIngelogd,
      fout,
      meldFout: setFout,
      ververs,
      ververstOp,
      wijzigingsteller,
    }),
    [
      ik,
      profiel,
      mag,
      kleuren,
      afdelingskleur,
      bouwnummers,
      boot,
      navigatie,
      karren,
      huidigeKar,
      kiesKar,
      laden,
      nietIngelogd,
      fout,
      ververs,
      ververstOp,
      wijzigingsteller,
    ],
  );

  return <Context.Provider value={waarde}>{children}</Context.Provider>;
}

export function useGegevens(): Toestand {
  const waarde = useContext(Context);
  if (!waarde) throw new Error('useGegevens moet binnen GegevensProvider gebruikt worden.');
  return waarde;
}

/** `0719-25-6504-10-01` → de segmenten, voor het kruimelpad. */
export const rechten = (ik: Ik | null): Rechten => ik?.rechten ?? GEEN_RECHTEN;

/** Nederlandse tijd, kort: `14:10`. */
export const tijd = (iso: string | undefined): string =>
  iso ? new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }) : '';

/** Nederlandse datum, kort: `6-8 14:10`. */
export const datumTijd = (iso: string | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getDate()}-${d.getMonth() + 1} ${tijd(iso)}`;
};

/** Uren met een decimale komma: `31,8 u`. */
export const uren = (waarde: number | undefined): string =>
  waarde == null ? '—' : `${waarde.toFixed(1).replace('.', ',')} u`;

/** Millimeters met een decimale komma en zonder overbodige nullen. */
export const mm = (waarde: number | null | undefined): string =>
  waarde == null ? '—' : String(Math.round(waarde * 100) / 100).replace('.', ',');

/** Percentage zonder decimalen: `38 %`. */
export const procent = (deel: number): string => `${Math.round(deel * 100)} %`;

/**
 * Het bouwnummer zoals het op de werkvloer wordt uitgesproken: `0719` → `719`.
 *
 * Alleen weergave. De voorloopnul blijft in de gegevens en in alle identificaties
 * staan (`0719-10-6112-07-01`, de exportbestanden, de API-paden); hij verdwijnt
 * uitsluitend daar waar het nummer als keuze of als kop op het scherm staat.
 */
export const bouwnummerKort = (nummer: string | null | undefined): string =>
  nummer ? nummer.replace(/^0+(?=\d)/, '') : '—';

/**
 * De naam van de boot zonder het bouwnummer erin.
 *
 * De namen uit de bron zijn `Yacht 0719`, `Feadship 1016`. Naast het nummer zelf
 * leest dat dubbel, dus blijft in de kiezer alleen het deel over dat wél iets
 * toevoegt: `Yacht`, `Feadship`. Staat er niets anders in de naam dan het nummer,
 * dan valt hij helemaal weg.
 */
export const bootnaamZonderNummer = (naam: string | undefined, nummer: string | undefined): string => {
  if (!naam) return '';
  const varianten = [nummer, bouwnummerKort(nummer)].filter(Boolean) as string[];
  let uit = naam;
  for (const variant of varianten) uit = uit.replace(new RegExp(`\\b0*${variant}\\b`, 'g'), ' ');
  return uit.replace(/\s{2,}/g, ' ').trim();
};

/** Initialen voor een avatar. */
export const initialen = (naam: string): string =>
  naam
    .split(/[\s.@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((deel) => deel[0]?.toUpperCase() ?? '')
    .join('') || '?';
